import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Select from 'react-select';
import { emphasize, makeStyles, useTheme } from 'pencil/styles';
import Typography from 'pencil/Typography';
import TextField from 'pencil/TextField';
import Paper from 'pencil/Paper';
import Chip from 'pencil/Chip';
import MenuItem from 'pencil/MenuItem';
import CancelIcon from 'pencil/icons/Cancel';
import FormHelperText from 'pencil/FormHelperText';
import CircularProgress from 'pencil/CircularProgress';
import Box from 'pencil/Box';
import isEqual from 'lodash/isEqual';
import * as I18n from 'circe/I18n';
import Tooltip from 'pencil/Tooltip';
import debounce from 'lodash/debounce';
import Creatable from 'react-select/creatable';

const useStyles = makeStyles((theme) => ({
  formControl: {
    position: 'relative',
    zIndex: 'modal',
  },
  root: {
    flexGrow: 1,
    height: 250,
    minWidth: 290,
  },
  input: {
    display: 'flex',
    padding: 0,
    height: 'auto',
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
  },
  chip: {
    margin: theme.spacing(0.5, 0.25),
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08,
    ),
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  singleValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: theme.colors.black,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    bottom: 6,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 40,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
  },
  error: {
    marginBottom: theme.spacing(2),
  },
  option: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
  },
  loader: {
    width: 14,
    height: 14,
    position: 'absolute',
    right: 68,
    top: '50%',
    marginTop: -10,
  },
}));

function NoOptionsMessage(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {I18n.str('typeToGetOptions', 'Type to get options')}
    </Typography>
  );
}

NoOptionsMessage.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props to be passed on to the wrapper.
   */
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};
function DropdownIndicator(props) {
  return (
    <Typography
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {I18n.str('typeToGetOptions', 'Type to get options')}
    </Typography>
  );
}

DropdownIndicator.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props to be passed on to the wrapper.
   */
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]),
};

function Control(props) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps },
  } = props;
  TextFieldProps.label = TextFieldProps.label
    ? TextFieldProps.label
    : I18n.str('select', 'Select...');
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps,
        },
      }}
      {...TextFieldProps}
    />
  );
}

Control.propTypes = {
  /**
   * Children to render.
   */
  children: PropTypes.node,
  /**
   * The mouse down event and the innerRef to pass down to the controller element.
   */
  innerProps: PropTypes.shape({
    onMouseDown: PropTypes.func.isRequired,
  }).isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]).isRequired,
  selectProps: PropTypes.object.isRequired,
};

function Option(props) {
  const classes = useStyles();

  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
      className={classes.option}
    >
      <Box fontWeight="bold">{props.children}</Box>
      {typeof props.data.subLabel !== 'undefined' && (
        <Typography variant="overline">{props.data.subLabel}</Typography>
      )}
    </MenuItem>
  );
}

Option.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * props passed to the wrapping element for the group.
   */
  innerProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    onMouseMove: PropTypes.func.isRequired,
    onMouseOver: PropTypes.func.isRequired,
    tabIndex: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * Inner ref to DOM Node
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]).isRequired,
  /**
   * Whether the option is focused.
   */
  isFocused: PropTypes.bool.isRequired,
  /**
   * Whether the option is selected.
   */
  isSelected: PropTypes.bool.isRequired,
};

function Placeholder(props) {
  const { selectProps, innerProps = {}, children } = props;
  return (
    <Typography color="textSecondary" className={selectProps.classes.placeholder} {...innerProps}>
      {children}
    </Typography>
  );
}

Placeholder.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * props passed to the wrapping element for the group.
   */
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

function SingleValue(props) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

SingleValue.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props passed to the wrapping element for the group.
   */
  innerProps: PropTypes.any.isRequired,
  selectProps: PropTypes.object.isRequired,
};

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

ValueContainer.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
};

function MultiValue(props) {
  return (
    <Tooltip title={props.children}>
      <Chip
        tabIndex={-1}
        label={props.children}
        className={clsx(props.selectProps.classes.chip, {
          [props.selectProps.classes.chipFocused]: props.isFocused,
        })}
        onDelete={props.removeProps.onClick}
        deleteIcon={<CancelIcon {...props.removeProps} />}
      />
    </Tooltip>
  );
}

MultiValue.propTypes = {
  children: PropTypes.node,
  isFocused: PropTypes.bool.isRequired,
  removeProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    onMouseDown: PropTypes.func.isRequired,
    onTouchEnd: PropTypes.func.isRequired,
  }).isRequired,
  selectProps: PropTypes.object.isRequired,
};

function Menu(props) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

Menu.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.element.isRequired,
  /**
   * Props to be passed to the menu wrapper.
   */
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

const Autocomplete = (props) => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    onChange,
    value,
    label,
    options,
    error,
    helperText,
    name,
    loading,
    isClearable,
    onKeyUp,
    onOptionChange,
    initialValue,
    isMulti = false,
    setFilterLabel,
    filterLabel,
    disabled = false,
    address = false,
  } = props;
  const defaultVal = value || initialValue;
  const [valueObject, setValueObject] = useState(defaultVal);

  const selectStyles = {
    input: (base) => ({
      ...base,
      color: theme.palette.text.primary,
      zIndex: 1500,
      '& input': {
        font: 'inherit',
      },
    }),
  };
  const handler = debounce((e) => {
    onKeyUp(e.target.value);
  }, 600);

  const onValueChange = (value) => {
    if (typeof setFilterLabel === 'function') {
      setFilterLabel(value);
    }
    if ((typeof value === 'undefined' || value === null || value === '') && !address) {
      onChange(null);
    } else {
      if (value && !isMulti && !address) {
        value = value.value;
        onChange(value);
      } else {
        if (address) {
          onChange(value);
        } else {
          onChange(value?.map((value) => value.value).join(','));
        }
      }
    }
    if (onOptionChange) {
      onOptionChange(value);
    }
  };

  useEffect(() => {
    const results = !isMulti
      ? options.find((option) => isEqual(option.value, value))
      : options.filter((option) => value?.split(',').includes(option.value));
    if (results) {
      setValueObject(results);
    } else if (value == null) {
      setValueObject(initialValue);
    }
  }, [value, options]);

  return (
    <div className={classes.formControl}>
      {loading === true && <CircularProgress size={18} className={classes.loader} />}
      <Creatable
        classes={classes}
        styles={selectStyles}
        formatCreateLabel={(inputValue) => {
          return I18n.str('newOptionValue', "Add '{inputValue}' as option", {
            inputValue,
          });
        }}
        inputId="react-select-single"
        creatable={address}
        isValidNewOption={(inputValue, selectValue, selectOptions) =>
          inputValue.trim().length > 0 && address
        }
        TextFieldProps={{
          label,
          InputLabelProps: {
            htmlFor: 'react-select-single',
            shrink: true,
          },
          onKeyUp: (e) => {
            if (onKeyUp && e.target.value) {
              e.persist();
              handler(e);
            }
          },
        }}
        name={name}
        placeholder=""
        options={options}
        components={components}
        value={filterLabel || isMulti ? filterLabel : valueObject}
        isDisabled={disabled}
        onChange={onValueChange}
        isClearable={isClearable}
        isMulti={isMulti}
      />
      {error === true && (
        <FormHelperText className={classes.error} error>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default Autocomplete;
